import React from 'react';
import { useTranslation } from 'react-i18next';
import { IParticipant } from '../service/review.service';
import { ParticipantCard } from './participant-card.component';

interface QueueListProps {
  participants: IParticipant[];
  myParticipantId?: number;
  slug?: string;
  waitEstimates?: { participantId: number; estimatedMinutes: number }[];
  privacyMode?: boolean;
}

export const QueueList = ({ participants, myParticipantId, slug, waitEstimates, privacyMode }: QueueListProps) => {
  const { t } = useTranslation();
  const active = participants.filter((p) => p.status === 'active');
  const waiting = participants.filter((p) => p.status === 'waiting').sort((a, b) => a.position - b.position);
  const done = participants.filter((p) => p.status === 'done');

  const nextUp = waiting.length > 0 ? [waiting[0]] : [];
  const standby = waiting.length > 1 ? [waiting[1]] : [];
  const rest = waiting.slice(2);

  return (
    <div className="flex flex-col gap-[8px] max-w-[500px] mx-auto">
      {active.length > 0 && (
        <div className="mb-[8px]">
          <p className="font-body text-[12px] font-semibold text-gray-900 uppercase tracking-wider mb-[6px] ml-[4px]">
            {t('queueList.inMeeting')}
          </p>
          {active.map((p) => (
            <ParticipantCard
              key={p.id}
              participant={p}
              isYou={p.id === myParticipantId}
              slug={slug}
              privacyMode={privacyMode && p.id !== myParticipantId}
            />
          ))}
        </div>
      )}

      {nextUp.length > 0 && (
        <div className="mb-[8px]">
          <p className="font-body text-[12px] font-semibold text-orange-700 uppercase tracking-wider mb-[6px] ml-[4px]">
            {t('queueList.nextUp')}
          </p>
          {nextUp.map((p) => (
            <div key={p.id} className="mb-[6px]">
              <ParticipantCard participant={p} isYou={p.id === myParticipantId} slug={slug} queueIndex={0} estimatedMinutes={waitEstimates?.find((e) => e.participantId === p.id)?.estimatedMinutes} privacyMode={privacyMode && p.id !== myParticipantId} />
            </div>
          ))}
        </div>
      )}

      {standby.length > 0 && (
        <div className="mb-[8px]">
          <p className="font-body text-[12px] font-semibold text-yellow-700 uppercase tracking-wider mb-[6px] ml-[4px]">
            {t('queueList.onStandby')}
          </p>
          {standby.map((p) => (
            <div key={p.id} className="mb-[6px]">
              <ParticipantCard participant={p} isYou={p.id === myParticipantId} slug={slug} queueIndex={1} estimatedMinutes={waitEstimates?.find((e) => e.participantId === p.id)?.estimatedMinutes} privacyMode={privacyMode && p.id !== myParticipantId} />
            </div>
          ))}
        </div>
      )}

      {rest.length > 0 && (
        <div className="mb-[8px]">
          <p className="font-body text-[12px] font-semibold text-gray-900 uppercase tracking-wider mb-[6px] ml-[4px]">
            {t('queueList.waiting')}
          </p>
          {rest.map((p, i) => (
            <div key={p.id} className="mb-[6px]">
              <ParticipantCard participant={p} isYou={p.id === myParticipantId} slug={slug} queueIndex={i + 2} estimatedMinutes={waitEstimates?.find((e) => e.participantId === p.id)?.estimatedMinutes} privacyMode={privacyMode && p.id !== myParticipantId} />
            </div>
          ))}
        </div>
      )}

      {done.length > 0 && (
        <div>
          <p className="font-body text-[12px] font-semibold text-gray-900 uppercase tracking-wider mb-[6px] ml-[4px]">
            {t('queueList.completed')}
          </p>
          {done.map((p) => (
            <div key={p.id} className="mb-[6px]">
              <ParticipantCard participant={p} isYou={p.id === myParticipantId} slug={slug} privacyMode={privacyMode && p.id !== myParticipantId} />
            </div>
          ))}
        </div>
      )}

      {participants.length === 0 && (
        <p className="font-body text-[16px] text-gray-900 text-center py-[32px]">
          {t('queueList.emptyState')}
        </p>
      )}
    </div>
  );
};
